/**
  * Represents a single replacement action.
  */
export default class Replacement {
  defaultProps = {
    designId: 0, // numeric design ID
    revisionUuid: '', // design revision UUID
    hotspotUuids: [], // array of hotspot UUIDs involved in this replacement
    replaceProductId: 0, // ID of product to be replaced
    swapInProductId: 0, // ID of product to be swapped in

    // The camera ID (string name) that swap was initiated from.
    // This is needed when User initiates a swap from a Modsy design,
    // we want to send user to the cloned Playground design with the
    // the same camera and hotspot selected.
    cameraId: '',

    // Internal status.
    // One of: NotStarted | InProgress | Completed | Error | Canceled
    status: 'NotStarted',

    // Error type when status is Error
    error: null
  };

  constructor (overrides) {
    this.fields = { ...this.defaultProps, ...overrides };
  }

  /** Creates a new Replacement copy with optional overrides. */
  makeCopy (overrides) {
    return new Replacement({ ...this.fields, ...overrides });
  }

  /**
   * Returns the camera id.
   * @returns {string} cameraId
   */
  cameraId () { return this.fields.cameraId; }

  /**
   * Returns the replacement designId.
   * @returns {number} designId
   */
  designId () { return this.fields.designId; }

  /**
   * Returns the array of replacement hotspot UUIDs.
   * @returns {string} hotspotUuids
   */
  hotspotUuids () { return this.fields.hotspotUuids; }

  /**
   * Returns the product ID being swapped out.
   * @returns {number} replaceProductId
   */
  replaceProductId () { return this.fields.replaceProductId; }

  /**
   * Returns the replacement swap-in-product ID.
   * @returns {number} swapInProductId
   */
  swapInProductId () { return this.fields.swapInProductId; }

  /**
   * Returns the replacement revision UUID.
   * @returns {string} revisionUuid
   */
  revisionUuid () { return this.fields.revisionUuid; }

  /**
   * Returns whether or not the replacement is in progress.
   * @returns {boolean}
   */
  isInProgress () { return this.fields.status === 'InProgress'; }

  /**
   * Returns whether or not the replacement has been canceled.
   * @returns {boolean}
   */
  hasBeenCanceled () { return this.fields.status === 'Canceled'; }

  /**
   * Returns whether or not the replacement is completed.
   * @returns {boolean}
   */
  isCompleted () { return this.fields.status === 'Completed'; }

  /**
   * Returns whether or not the replacement was initiated from a modsy created design
   * or a playground design
   */
  isNewDesign () { return this.fields.isNewDesign; }
}
